import React from 'react';
import { SimWorld } from '../../simulation/types/SimWorld';
import type { SimNPC } from '../../simulation/types/SimNPC';

interface WorldStatsPanelProps {
  world: SimWorld;
  onNPCClick?: (npc: SimNPC) => void;
  isMobile?: boolean;
}

export const WorldStatsPanel: React.FC<WorldStatsPanelProps> = ({ world, onNPCClick, isMobile = false }) => {
  const { npcs, day } = world;

  const alive = npcs.filter((n) => n.isAlive);
  const dead = npcs.filter((n) => !n.isAlive);
  const partnered = npcs.filter((n) => n.hasPartner);
  const totalDuels = npcs.reduce((sum, n) => sum + n.wins + n.losses, 0) / 2;

  // 世代統計を計算
  const generationCounts = npcs.reduce((acc, npc) => {
    const gen = npc.generation || 1;
    acc[gen] = (acc[gen] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);
  const maxGeneration = Math.max(...Object.keys(generationCounts).map(Number));

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>🌍 World Stats</h3>

      <div style={styles.stats}>
        <div style={styles.statItem}>
          <span style={styles.label}>Day:</span>
          <span style={styles.value}>{day}</span>
        </div>

        <div style={styles.statItem}>
          <span style={styles.label}>Population:</span>
          <span style={styles.value}>{npcs.length}</span>
        </div>

        <div style={styles.statItem}>
          <span style={styles.label}>Alive:</span>
          <span style={styles.value}>{alive.length}</span>
        </div>

        <div style={styles.statItem}>
          <span style={styles.label}>Dead:</span>
          <span style={styles.value}>{dead.length}</span>
        </div>

        <div style={styles.statItem}>
          <span style={styles.label}>Partnered:</span>
          <span style={styles.value}>{partnered.length}</span>
        </div>

        <div style={styles.statItem}>
          <span style={styles.label}>Total Duels:</span>
          <span style={styles.value}>{totalDuels}</span>
        </div>

        <div style={styles.statItem}>
          <span style={styles.label}>Max Generation:</span>
          <span style={styles.value}>第{maxGeneration}世代</span>
        </div>
      </div>

      <div style={styles.npcList}>
        <h4 style={styles.subtitle}>NPCs</h4>
        {alive.map((npc) => (
          <div
            key={npc.id}
            style={{
              ...styles.npcItem,
              cursor: onNPCClick ? 'pointer' : 'default',
            }}
            onClick={() => onNPCClick?.(npc)}
          >
            <span style={{
              ...styles.npcName,
              textDecoration: onNPCClick ? 'underline' : 'none',
              textDecorationColor: '#ccc',
            }}>{npc.name} <span style={styles.genBadge}>[第{npc.generation || 1}世代]</span></span>
            <span style={styles.npcStats}>
              ❤️{npc.health} ⭐{npc.rating}
            </span>
          </div>
        ))}
        {dead.map((npc) => (
          <div
            key={npc.id}
            style={{
              ...styles.npcItem,
              ...styles.deadNPC,
              cursor: onNPCClick ? 'pointer' : 'default',
            }}
            onClick={() => onNPCClick?.(npc)}
          >
            <span style={{
              ...styles.npcName,
              textDecoration: onNPCClick ? 'underline' : 'none',
              textDecorationColor: '#ccc',
            }}>💀 {npc.name} <span style={styles.genBadge}>[第{npc.generation || 1}世代]</span></span>
            <span style={styles.npcStats}>Day {npc.daysLived}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  title: {
    margin: '0 0 15px 0',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  stats: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
  },
  statItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
    background: '#f5f5f5',
    borderRadius: '6px',
  },
  label: {
    fontWeight: 'bold',
    color: '#666',
  },
  value: {
    color: '#333',
    fontWeight: 'bold',
  },
  npcList: {
    borderTop: '2px solid #eee',
    paddingTop: '15px',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  subtitle: {
    margin: '0 0 10px 0',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#666',
  },
  npcItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px',
    marginBottom: '6px',
    background: '#f9f9f9',
    borderRadius: '4px',
    fontSize: '13px',
  },
  npcName: {
    fontWeight: 'bold',
  },
  npcStats: {
    color: '#666',
    fontSize: '12px',
  },
  deadNPC: {
    opacity: 0.5,
  },
  genBadge: {
    fontSize: '10px',
    color: '#888',
    fontWeight: 'normal',
  },
};
